import {
    IMappingProfile,
    IQueryParams,
    IQueryParamsSymbol,
    PFWmsEntities,
} from '@pf/shared-common';
import { createMap, Mapper } from '@automapper/core';
import {
    entitySymbols,
    withDefaultEntityMetadata,
    withDefaultSearchParamsMappingConfiguration,
    withDefaultSearchParamsMetadata,
    withFragmentSearchFilter,
    withMetadata,
    withSearchFilter,
    withSearchFilterUnTyped,
} from '@pf/shared-utility';
import {
    InventoryBundleCreate,
    InventoryBundleDto,
    InventoryBundleRead,
    InventoryBundleSearchParams,
} from '../../entities/inventory-bundle.dto';
import {
    CurrencySymbol,
    CustomFieldMetadataMap,
    DefaultCustomerFragmentMetadataMap,
    DefaultVendorFragmentMetadataMap,
    ResourceAuthSymbol,
} from '@pf/shared/util-platform';
import { ContainerMetadataMap } from '../containers/container.mapping-profile';
import { ItemMetadataMap } from '@pf/platform-item-catalog/domain';
import { withWarehouseSearchFilter } from '../wms-mapping.util';

const symbols = entitySymbols(PFWmsEntities.InventoryBundle);

withDefaultEntityMetadata<Omit<InventoryBundleDto, 'name'>>(symbols.dto, {
    availableQuantity: Number,
    averageUnitCost: CurrencySymbol,
    condition: String,
    container: ContainerMetadataMap.symbols.dto,
    customFields: CustomFieldMetadataMap.symbols.dto,
    customer: DefaultCustomerFragmentMetadataMap.symbols.dto,
    inventoryBundleInstances: [Object],
    inventoryBundleReservations: [Object],
    item: ItemMetadataMap.symbols.dto,
    reservedQuantity: Number,
    resourceAuth: ResourceAuthSymbol,
    status: String,
    totalQuantity: Number,
    vendor: DefaultVendorFragmentMetadataMap.symbols.dto,
    warehouse: Object,
});

withDefaultEntityMetadata<InventoryBundleRead>(symbols.entity, {
    availableQuantity: Number,
    averageUnitCost: CurrencySymbol,
    condition: String,
    container: ContainerMetadataMap.symbols.entity,
    customFields: CustomFieldMetadataMap.symbols.entity,
    customer: DefaultCustomerFragmentMetadataMap.symbols.entity,
    inventoryBundleInstances: [Object],
    inventoryBundleReservations: [Object],
    item: ItemMetadataMap.symbols.entity,
    reservedQuantity: Number,
    resourceAuth: ResourceAuthSymbol,
    status: String,
    totalQuantity: Number,
    vendor: DefaultVendorFragmentMetadataMap.symbols.entity,
    warehouse: Object,
});

withMetadata<InventoryBundleCreate>(symbols.createBody, {
    averageUnitCost: CurrencySymbol,
    condition: String,
    containerId: String,
    customerId: String,
    inventoryBundleReservations: [Object],
    itemId: String,
    reservedForLocationId: String,
    resourceAuth: ResourceAuthSymbol,
    status: String,
    totalQuantity: Number,
    vendorId: String,
    warehouseId: String,
});

withDefaultSearchParamsMetadata<InventoryBundleSearchParams>(
    symbols.searchParams,
    {
        status: String,
        itemId: String,
        itemName: String,
        itemCode: String,
        containerId: String,
        zoneId: String,
        slotId: String,
        warehouseId: String,
    }
);

export const InventoryBundleMetadataMap: IMappingProfile = {
    symbols,
    profile: (mapper: Mapper) => {
        createMap<InventoryBundleRead, InventoryBundleDto>(
            mapper,
            symbols.entity,
            symbols.dto
        );
        createMap<InventoryBundleDto, InventoryBundleRead>(
            mapper,
            symbols.dto,
            symbols.entity
        );
        createMap<InventoryBundleDto, InventoryBundleCreate>(
            mapper,
            symbols.dto,
            symbols.createBody
        );
        createMap<
            IQueryParams<InventoryBundleDto>,
            InventoryBundleSearchParams
        >(
            mapper,
            IQueryParamsSymbol,
            symbols.searchParams,
            withSearchFilter('status'),
            withFragmentSearchFilter('item'),
            withSearchFilterUnTyped('containerId'),
            withSearchFilterUnTyped('slotId'),
            withWarehouseSearchFilter(),
            ...withDefaultSearchParamsMappingConfiguration()
        );

        ItemMetadataMap.profile(mapper);
        ContainerMetadataMap.profile(mapper);
        DefaultCustomerFragmentMetadataMap.profile(mapper);
        DefaultVendorFragmentMetadataMap.profile(mapper);
        CustomFieldMetadataMap.profile(mapper);
    },
};
